"use client";

import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { format } from 'date-fns';
import { vi } from 'date-fns/locale';

import CalendarDaysMock from '../components/calendar-days-mock';
import ShiftAssignmentCreateModal from '../components/shift-assignment-create-modal';
import { useScheduleInteraction } from '../hooks/useScheduleInteraction';
import { useWeekNavigation } from '../hooks/useWeekNavigation';
import EmployeeScheduleRow from '../components/shift-employee-schedule-row';
import RemoveShiftModal from '../components/shift-remove-modal';
import { useDetectOutsideClick } from '../../../hooks/useDetectOutsideClick';
import { isSuccess } from '../../../models/response/base-response';
import { showToast } from '../../../components/custom/custom-toast';
import QuickConfirmAssignModal from '../components/shift-quick-confirm-assign-modal';
import { useStoreOptions } from '../../../hooks/use-store-options';


// 💡 IMPORT SERVICE VÀ TYPES TỪ API
import ShiftAssigmentService, { ShiftStatusCode } from '../services/shift-assignment-service';
import {
    EmployeeShiftCalendar,
    ShiftListFilters,
    ShiftCalendar
} from '../types/shift-assignment.types';

const ITEMS_PER_PAGE_OPTIONS = [10, 20, 50, 100];
const DEFAULT_ITEMS_PER_PAGE = 10;

// === HOOKS NAVIGATION VÀ INTERACTION ===
const ShiftAssignmentIndexPage: React.FC = () => {
    const {
        currentDate,
        weekDays,
        weekTitle,
        isCalendarVisible,
        setIsCalendarVisible,
        handleWeekSelect,
        handleMonthChange,
    } = useWeekNavigation(new Date());

    const [allEmployeeSchedules, setAllEmployeeSchedules] = useState<EmployeeShiftCalendar[]>([]);
    const [totalRecords, setTotalRecords] = useState(0);
    const [shifts, setShifts] = useState<ShiftCalendar[]>([]);
    const [isLoadingShifts, setIsLoadingShifts] = useState(true);

    // === CHI NHÁNH: lọc danh sách nhân viên (chỉ NV làm theo ca) theo chi nhánh — LUÔN phải chọn
    // đúng 1 chi nhánh cụ thể (bỏ option "Tất cả chi nhánh") để tránh mơ hồ khi bấm "+" gán ca ngay
    // trên lưới: gán vào chi nhánh nào. Tự chọn chi nhánh đầu tiên khi danh sách tải xong.
    const { options: branchOptions, loading: isLoadingBranches } = useStoreOptions();
    const [selectedBranchId, setSelectedBranchId] = useState<number | ''>('');
    useEffect(() => {
        if (!selectedBranchId && branchOptions.length > 0) {
            setSelectedBranchId(branchOptions[0].value);
        }
    }, [branchOptions, selectedBranchId]);

    // === STATE PHÂN TRANG & FILTER ===
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(DEFAULT_ITEMS_PER_PAGE);
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');

    const {
        fetch: fetchCalendarList,
        loading: isLoadingCalendar
    } = ShiftAssigmentService.listCalendar();

    const {
        fetch: fetchShiftList,
        loading: apiIsLoadingShifts
    } = ShiftAssigmentService.listShift();

    const {
        activePopover,
        activeActionMenu,
        removeModalState,
        createModalState,
        isBatchModalOpen,
        quickConfirmAssignState,
        isDeleting,
        setActivePopover,
        setActiveActionMenu,
        setQuickConfirmAssignState,
        setRemoveModalState,
        setCreateModalState,
        setIsBatchModalOpen,
        handleOpenPopover,
        handleShiftSelect,
        handleOpenActionMenu,
        handleRemoveShift,
        handleOpenCreateModal,
        handleConfirmRemove,
        handleSingleShiftSubmit,
        handleBatchSubmit,
        handleQuickConfirmAssign,
    } = useScheduleInteraction(allEmployeeSchedules, (weekStart: Date) => loadCalendar(weekStart), weekDays, selectedBranchId || undefined);

    const calendarRef = useRef<HTMLDivElement>(null);
    useDetectOutsideClick(calendarRef, () => setIsCalendarVisible(false));
    // Modal "gán ca" (batch/create/edit) KHÔNG tự đóng khi bấm outside — form có nhiều bước nhập
    // (chọn NV, ngày, ghi chú...), bấm nhầm ra ngoài (kể cả vào popup date picker) trước đây làm mất
    // hết dữ liệu đang nhập. Chỉ đóng qua nút "✕"/hủy trong modal.
    const batchModalRef = useRef<HTMLDivElement>(null);
    const createModalRef = useRef<HTMLDivElement>(null);
    const removeModalRef = useRef<HTMLDivElement>(null);
    useDetectOutsideClick(removeModalRef, () => setRemoveModalState(null));

    const fetchListRef = useRef(fetchCalendarList);
    useEffect(() => {
        fetchListRef.current = fetchCalendarList;
    }, [fetchCalendarList]);

    // === LOGIC TẢI DỮ LIỆU LỊCH LÀM VIỆC ===
    const loadCalendar = useCallback(async (weekStart: Date) => {
        const start_date = format(weekStart, 'dd/MM/yyyy');
        const end_date = format(weekDays[weekDays.length - 1], 'dd/MM/yyyy');

        const params: ShiftListFilters & { q?: string } = {
            start_date,
            end_date,
            ...(selectedBranchId ? { branch_id: selectedBranchId } : {}),
        };

        const res = await fetchListRef.current(params);

        if (res && isSuccess(res) && res.data) {
            const list = Array.isArray(res.data) ? res.data : (res.data as any).list || [];
            const total_record = Array.isArray(res.data) ? list.length : (res.data as any).total_record || 0;

            setAllEmployeeSchedules(list);
            setTotalRecords(total_record);
        } else {
            setAllEmployeeSchedules([]);
            setTotalRecords(0);
            showToast({ message: "Lấy dữ liệu lịch làm việc thất bại", type: "error" });
        }
    }, [weekDays, selectedBranchId]);

    const loadShifts = useCallback(async () => {
        setIsLoadingShifts(true);
        // Chỉ lấy ca ĐANG HOẠT ĐỘNG — ca đã tạm ngưng/đã xoá không được xuất hiện trong danh sách chọn để gán.
        const res = await fetchShiftList({ status: ShiftStatusCode.ACTIVE });
        if (res && isSuccess(res) && Array.isArray((res.data as any).list)) {
            setShifts((res.data as any).list);
        } else {
            showToast({ message: "Lấy danh sách ca làm việc thất bại", type: "error" });
            setShifts([]);
        }
        setIsLoadingShifts(false);
    }, [fetchShiftList]);

    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedSearchTerm(searchTerm);
        }, 1000);

        return () => {
            clearTimeout(handler);
        };
    }, [searchTerm]);

    // 💡 GỌI API LỊCH LÀM VIỆC
    useEffect(() => {
        void loadCalendar(weekDays[0]);
        void loadShifts();
    }, [weekDays, loadCalendar]);

    // === LOGIC PHÂN TRANG/TÌM KIẾM ===
    // 1. Lọc danh sách nhân viên
    const filteredSchedules = useMemo(() => {
        if (!debouncedSearchTerm) {
            return allEmployeeSchedules;
        }
        return allEmployeeSchedules.filter(schedule =>
            schedule.employee.name.toLowerCase().includes(debouncedSearchTerm.toLowerCase())
        );
    }, [allEmployeeSchedules, debouncedSearchTerm]);

    // 2. Phân trang trên danh sách
    const employeeSchedules = useMemo(() => {
        const startIndex = (currentPage - 1) * itemsPerPage;
        const endIndex = startIndex + itemsPerPage;
        return filteredSchedules.slice(startIndex, endIndex);
    }, [filteredSchedules, currentPage, itemsPerPage]);

    const totalPages = useMemo(() => Math.ceil(filteredSchedules.length / itemsPerPage), [filteredSchedules.length, itemsPerPage]);
    const startIndex = (currentPage - 1) * itemsPerPage + 1;
    const endIndex = Math.min(currentPage * itemsPerPage, filteredSchedules.length);

    const handlePageChange = useCallback((page: number) => {
        setCurrentPage(page);
    }, []);

    const handleItemsPerPageChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
        setItemsPerPage(Number(e.target.value));
        setCurrentPage(1);
    }, []);

    const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchTerm(e.target.value);
        setCurrentPage(1); // Reset về trang 1 khi tìm kiếm thay đổi
    }, []);

    const handleAssignmentSuccess = useCallback(async () => {
        await loadCalendar(weekDays[0]);
    }, [loadCalendar, weekDays]);

    const handleToggleCalendarVisibility = useCallback(() => {
        setIsCalendarVisible(prev => !prev);
    }, []);

    const handleOpenBatchModal = useCallback(() => {
        setIsBatchModalOpen(true);
    }, []);

    const handleCloseBatchModal = useCallback(() => {
        setIsBatchModalOpen(false);
    }, []);

    const handleCloseCreateModal = useCallback(() => {
        setCreateModalState(null);
    }, []);

    const handleCloseQuickConfirmAssignModal = useCallback(() => {
        setQuickConfirmAssignState(null);
    }, []);

    const handleCloseRemoveModal = useCallback(() => {
        setRemoveModalState(null);
    }, []);

    const handleRemoveSingleShift = useCallback(() => handleRemoveShift('single'), [handleRemoveShift]);
    const handleRemoveMultipleShifts = useCallback(() => handleRemoveShift('multiple'), [handleRemoveShift]);

    return (
        <div className="space-y-6 p-4 md:p-6 bg-gray-50 min-h-screen">
            <div className="bg-white rounded-lg shadow-md border">

                {/* 1. Header Chức Năng */}
                <div className="flex justify-between items-center p-4 border-b">
                    <div className="flex items-center space-x-4 relative">
                        <div className="flex border border-gray-300 rounded-xl overflow-hidden shadow-sm bg-white">
                            <button
                                className="flex items-center px-4 py-3 font-bold text-gray-800 text-lg whitespace-nowrap 
                                      hover:bg-blue-50 hover:text-blue-700 transition duration-150 ease-in-out"
                                onClick={handleToggleCalendarVisibility}
                            >
                                {weekTitle}
                                <span className="ml-3 text-xl text-blue-500">📅</span>
                            </button>
                        </div>

                        {isCalendarVisible && (
                            <div ref={calendarRef} id="week-calendar-popup" className="absolute z-50 top-full mt-2 left-0">
                                <CalendarDaysMock currentDate={currentDate} selectedDate={currentDate} onSelectWeek={handleWeekSelect} onMonthChange={handleMonthChange} />
                            </div>
                        )}
                    </div>

                    {/* KHU VỰC NÚT VÀ TÌM KIẾM MỚI */}
                    <div className="flex items-center space-x-3">
                        {/* CHỌN CHI NHÁNH: lọc danh sách nhân viên (chỉ NV làm theo ca) theo chi nhánh */}
                        <select
                            className="border p-2 rounded-md outline-none"
                            value={selectedBranchId}
                            onChange={(e) => setSelectedBranchId(e.target.value ? Number(e.target.value) : '')}
                            disabled={isLoadingBranches}
                        >
                            {branchOptions.map((option) => (
                                <option key={option.value} value={option.value}>{option.label}</option>
                            ))}
                        </select>

                        {/* INPUT TÌM KIẾM */}
                        <input
                            type="text"
                            placeholder="Tìm kiếm..."
                            className="border p-2 rounded-md outline-none"
                            value={searchTerm}
                            onChange={handleSearchChange}
                        />

                        {/* NÚT GÁN NHIỀU NHÂN VIÊN */}
                        <button
                            onClick={handleOpenBatchModal}
                            className="group inline-flex items-center gap-2 rounded-xl bg-green-600 px-6 py-3 text-sm font-bold text-white shadow-lg shadow-green-200 transition-all hover:bg-green-700 hover:-translate-y-0.5"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5 transition-transform group-hover:rotate-90">
                                <path fillRule="evenodd" d="M12 3.75a.75.75 0 01.75.75v6.75h6.75a.75.75 0 010 1.5h-6.75v6.75a.75.75 0 01-1.5 0v-6.75H4.5a.75.75 0 010-1.5h6.75V4.5a.75.75 0 01.75-.75z" clipRule="evenodd" />
                            </svg>
                            <span>GÁN NHIỀU NHÂN VIÊN</span>
                        </button>
                    </div>
                </div>

                {/* 2. Lưới Lịch Làm Việc (Phần chính) */}
                <div className="overflow-x-auto">
                    <div className="min-w-full inline-block align-middle">
                        <div className="grid border-gray-400 border-b"
                            style={{ gridTemplateColumns: 'minmax(200px, 1fr) repeat(7, minmax(100px, 1fr))' }}>

                            {/* HÀNG TIÊU ĐỀ (HEADER ROW) */}
                            <div className="p-3 font-bold text-gray-700 bg-gray-50 border-r border-b border-gray-400">Tên nhân viên</div>
                            {weekDays.map((day) => (
                                <div key={day.toISOString()} className="p-3 text-center font-semibold text-gray-700 bg-gray-50 border-r border-b border-gray-400 last:border-r-0">
                                    <div className="text-2xl">{format(day, 'dd')}</div>
                                    <div className="text-xs font-normal text-gray-500">{format(day, 'E', { locale: vi as any })} {format(day, 'MMM', { locale: vi as any })}</div>
                                </div>
                            ))}

                            {/* HÀNG DỮ LIỆU NHÂN VIÊN (Dữ liệu thực) */}
                            {isLoadingCalendar ? (
                                <div className="col-span-8 p-4 text-center text-gray-500">Đang tải lịch làm việc...</div>
                            ) : employeeSchedules.length === 0 ? (
                                <div className="col-span-8 p-4 text-center text-gray-500">Không tìm thấy lịch làm việc.</div>
                            ) : (
                                employeeSchedules.map((employeeSchedule, index) => (
                                    <EmployeeScheduleRow
                                        key={employeeSchedule.employee.id}
                                        employeeSchedule={employeeSchedule as any}
                                        weekDays={weekDays}
                                        onOpenCreateModal={handleOpenCreateModal}
                                        shifts={shifts}
                                        isLoadingShifts={isLoadingShifts}

                                        activePopover={activePopover}
                                        onOpenPopover={handleOpenPopover}
                                        onShiftSelect={handleShiftSelect}

                                        activeActionMenu={activeActionMenu}
                                        onOpenActionMenu={handleOpenActionMenu}
                                        onRemoveSingle={handleRemoveSingleShift}
                                        onRemoveMultiple={handleRemoveMultipleShifts}
                                        rowIndex={index}
                                        totalRows={employeeSchedules.length}
                                    />
                                ))
                            )}
                        </div>
                    </div>
                </div>

                {/* 3. Thanh Chân Trang/Phân Trang */}
                <div className="flex justify-between items-center p-4 pt-4 border-t">
                    <p className="text-sm text-gray-600">
                        Hiển thị <span className="font-semibold">{filteredSchedules.length > 0 ? startIndex : 0}</span> đến <span className="font-semibold">{endIndex}</span> của <span className="font-semibold">{filteredSchedules.length}</span> hàng
                        <select
                            className="ml-2 border rounded-md p-1"
                            value={itemsPerPage}
                            onChange={handleItemsPerPageChange}
                        >
                            {ITEMS_PER_PAGE_OPTIONS.map(option => (
                                <option key={option} value={option}>{option}</option>
                            ))}
                        </select>
                    </p>

                    <div className="flex space-x-1">
                        <button
                            className="w-8 h-8 flex items-center justify-center border rounded-full text-gray-500 hover:bg-gray-100 disabled:opacity-50"
                            onClick={() => handlePageChange(currentPage - 1)}
                            disabled={currentPage === 1}
                        >
                            {'<'}
                        </button>

                        {/* Hiển thị phân trang */}
                        {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => {
                            const isCurrent = page === currentPage;
                            if (page === 1 || page === totalPages || Math.abs(page - currentPage) <= 1) {
                                return (
                                    <button
                                        key={page}
                                        className={`w-8 h-8 flex items-center justify-center rounded-full transition ${isCurrent ? 'bg-blue-600 text-white' : 'border text-gray-700 hover:bg-gray-100'
                                            }`}
                                        onClick={() => handlePageChange(page)}
                                    >
                                        {page}
                                    </button>
                                );
                            } else if (
                                (page === 2 && currentPage > 3) ||
                                (page === totalPages - 1 && currentPage < totalPages - 2)
                            ) {
                                return <span key={page} className="w-8 h-8 flex items-center justify-center text-gray-500">...</span>;
                            }
                            return null;
                        })}


                        <button
                            className="w-8 h-8 flex items-center justify-center border rounded-full text-gray-700 hover:bg-gray-100 disabled:opacity-50"
                            onClick={() => handlePageChange(currentPage + 1)}
                            disabled={currentPage === totalPages || totalPages === 0}
                        >
                            {'>'}
                        </button>
                    </div>
                </div>

            </div>

            {/* 4. MODAL GÁN CA HÀNG LOẠT */}
            {isBatchModalOpen && (
                <ShiftAssignmentCreateModal
                    ref={batchModalRef}
                    onClose={handleCloseBatchModal}
                    onSubmit={handleBatchSubmit}
                    initialValues={{}}
                    mode="batch"
                    onSuccess={handleAssignmentSuccess}
                    shifts={shifts}
                    isLoadingShifts={isLoadingShifts}
                    branchId={selectedBranchId || undefined}
                />
            )}

            {/* 5. MODAL PHÂN CÔNG ĐƠN LẺ */}
            {createModalState?.isOpen && (
                <ShiftAssignmentCreateModal
                    ref={createModalRef}
                    onClose={handleCloseCreateModal}
                    onSuccess={handleAssignmentSuccess}
                    onSubmit={handleSingleShiftSubmit}
                    initialValues={createModalState.initialValues}
                    mode={createModalState.mode}
                    shifts={shifts}
                    isLoadingShifts={isLoadingShifts}
                    branchId={selectedBranchId || undefined}
                />
            )}

            {/* 💡 MODAL XÁC NHẬN GÁN CA NHANH */}
            {quickConfirmAssignState?.isOpen && (
                <QuickConfirmAssignModal
                    onClose={handleCloseQuickConfirmAssignModal}
                    onConfirm={handleQuickConfirmAssign}
                    data={quickConfirmAssignState.data}
                />
            )}

            {/* 6. MODAL XÁC NHẬN GỠ PHÂN CÔNG */}
            {removeModalState && (
                <RemoveShiftModal
                    ref={removeModalRef}
                    onClose={handleCloseRemoveModal}
                    onConfirm={handleConfirmRemove}
                    mode={removeModalState.mode}
                    isDeleting={isDeleting}
                    initialDate={removeModalState.data.day}
                />
            )}
        </div>
    );
};

export default ShiftAssignmentIndexPage;